




































































import { Component, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import moment from 'moment';
import { Customer } from '@/models/customer';
import { Station } from '@/models/station';
import { filterReportStations } from '@/misc/reportCustomerFilter';
import { getCustomerName } from '@/misc/stationUtils';

const StationStore = namespace('station');

interface MaintenanceCalendarEvent {
  id: string;
  stationId: string;
  customerId: string;
  title: string;
  subtitle: string;
  date: string;
  isOverdue: boolean;
  actuatorCount: number;
}

interface ProjectCardRow {
  stationId: string;
  customerId: string;
  stationName: string;
  customerName: string;
  description: string;
  activeMaintenanceCount: number;
  openMaintenanceCount: number;
  overdueMaintenanceCount: number;
  nextMaintenance: string | null;
  color: string;
}

const CALENDAR_RANGE_MONTHS = 12;
const PROJECT_CARD_COLORS = ['#1A73E8', '#4CAF50', '#f57c00', '#e91e63', '#344767', '#9c27b0'];

@Component({
  components: {
    ReportsNav: () => import('@/components/ReportsNav.component.vue'),
    ReportsPageHeader: () => import('@/components/ReportsPageHeader.component.vue'),
    ProjectReportCard: () => import('@/components/ProjectReportCard.component.vue'),
    MaintenanceCalendarTimeline: () =>
      import('@/components/MaintenanceCalendarTimeline.component.vue'),
  },
})
export default class ReportsProjectsView extends Vue {
  @StationStore.Action('loadAllStations')
  private loadAllStations!: () => Promise<Station[]>;

  @StationStore.Getter('allStations')
  private allStations!: Station[];

  public projectsGridLoading = true;
  public projectSearch = '';
  public calendarRangeMonths = CALENDAR_RANGE_MONTHS;

  public get calendarLoading(): boolean {
    return this.projectsGridLoading;
  }

  public get calendarEvents(): MaintenanceCalendarEvent[] {
    const rangeEnd = moment().add(CALENDAR_RANGE_MONTHS, 'months').endOf('month');
    const today = moment().startOf('day');
    const events: MaintenanceCalendarEvent[] = [];

    for (const station of this.reportStations) {
      const customerId = this.getCustomerId(station);
      const customerName = getCustomerName(station);

      const maintenanceMachines = station.machines.filter(
        (machine) => machine.maintenanceActive && machine.nextMaintenance,
      );

      const relevantMachines = maintenanceMachines.filter((machine) => {
        const date = moment(machine.nextMaintenance, 'YYYY-MM-DD');
        return !date.isAfter(rangeEnd, 'day');
      });

      if (!relevantMachines.length) continue;

      const earliestDate = relevantMachines
        .map((machine) => moment(machine.nextMaintenance, 'YYYY-MM-DD'))
        .sort((a, b) => a.valueOf() - b.valueOf())[0];

      events.push({
        id: station.id,
        stationId: station.id,
        customerId,
        title: station.name,
        subtitle: customerName,
        date: earliestDate.format('YYYY-MM-DD'),
        isOverdue: earliestDate.isBefore(today, 'day'),
        actuatorCount: relevantMachines.length,
      });
    }

    return events.sort((a, b) => a.date.localeCompare(b.date));
  }

  public get reportStations(): Station[] {
    return filterReportStations(this.allStations);
  }

  public get projectCards(): ProjectCardRow[] {
    const currentDate = moment().endOf('day');

    return this.reportStations
      .map((station) => {
        const activeMachines = station.machines.filter((machine) => machine.maintenanceActive);
        const openMachines = activeMachines.filter((machine) => {
          if (!machine.nextMaintenance) return false;
          const nextMaintenance = moment(machine.nextMaintenance, 'YYYY-MM-DD');
          return nextMaintenance.isSameOrBefore(currentDate, 'day');
        });
        const overdueMachines = activeMachines.filter((machine) => {
          if (!machine.nextMaintenance) return false;
          return moment(machine.nextMaintenance, 'YYYY-MM-DD').isBefore(moment(), 'day');
        });

        const nextMaintenanceDates = activeMachines
          .map((machine) => machine.nextMaintenance)
          .filter(Boolean)
          .sort();

        const location = [station.city, station.country].filter(Boolean).join(', ');
        const actuatorLabel = station.machines.length === 1 ? 'Antrieb' : 'Antriebe';

        return {
          stationId: station.id,
          customerId: this.getCustomerId(station),
          stationName: station.name,
          customerName: getCustomerName(station),
          description: [location, `${station.machines.length} ${actuatorLabel}`]
            .filter(Boolean)
            .join(' · '),
          activeMaintenanceCount: activeMachines.length,
          openMaintenanceCount: openMachines.length,
          overdueMaintenanceCount: overdueMachines.length,
          nextMaintenance: nextMaintenanceDates[0] ?? null,
          color: this.getProjectCardColor(station.id),
        };
      })
      .sort((a, b) => {
        if (b.openMaintenanceCount !== a.openMaintenanceCount) {
          return b.openMaintenanceCount - a.openMaintenanceCount;
        }
        return a.stationName.localeCompare(b.stationName, 'de');
      });
  }

  public get filteredProjectCards(): ProjectCardRow[] {
    const query = this.projectSearch.trim().toLowerCase();
    if (!query) return this.projectCards;

    return this.projectCards.filter(
      (project) =>
        project.stationName.toLowerCase().includes(query)
        || project.customerName.toLowerCase().includes(query),
    );
  }

  public get allProjectsSubtitle(): string {
    const count = this.projectCards.length;
    return `${count} ${count === 1 ? 'Projekt' : 'Projekte'} mit Wartungs- und Antriebsinformationen.`;
  }

  public get allProjectsEmptyText(): string {
    if (this.projectSearch.trim()) {
      return 'Keine Projekte für diese Suche gefunden';
    }
    return 'Keine Projektdaten verfügbar';
  }

  private getCustomerId(station: Station): string {
    const customer = station.customer;
    if (!customer || typeof customer === 'string') return '';
    return (customer as Customer).id;
  }

  private getProjectCardColor(stationId: string): string {
    let hash = 0;
    for (let i = 0; i < stationId.length; i++) {
      hash = stationId.charCodeAt(i) + ((hash << 5) - hash);
    }
    return PROJECT_CARD_COLORS[Math.abs(hash) % PROJECT_CARD_COLORS.length];
  }

  public projectRoute(project: ProjectCardRow) {
    return {
      name: 'station_details_view',
      params: {
        customerId: project.customerId,
        stationId: project.stationId,
      },
    };
  }

  public async mounted() {
    try {
      await this.loadAllStations();
    } catch {
      // keep defaults
    } finally {
      this.projectsGridLoading = false;
    }
  }
}
